import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TrendingUp, TrendingDown, Wallet, Receipt, CreditCard, Building2, Upload, FileText, Trash2, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function FinanzePage() {
  const [data, setData] = useState(null);
  const [annuale, setAnnuale] = useState([]);
  const [fattureXml, setFattureXml] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [xmlDetailOpen, setXmlDetailOpen] = useState(false);
  const [selectedXml, setSelectedXml] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [sitRes, annRes, xmlRes] = await Promise.all([
        axios.get(`${API}/situazione-finanziaria`, { withCredentials: true }),
        axios.get(`${API}/fatturato-annuale`, { withCredentials: true }),
        axios.get(`${API}/fatture-xml`, { withCredentials: true })
      ]);
      setData(sitRes.data);
      setAnnuale(annRes.data);
      setFattureXml(xmlRes.data);
    } catch (error) { toast.error('Errore nel caricamento'); }
    finally { setLoading(false); }
  };

  const handleUploadXml = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xml')) {
      toast.error('Seleziona un file XML');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/fatture-xml/upload`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Fattura XML importata');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel parsing XML');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteXml = async (id) => {
    try {
      await axios.delete(`${API}/fatture-xml/${id}`, { withCredentials: true });
      toast.success('Fattura eliminata');
      fetchAll();
    } catch { toast.error('Errore'); }
  };

  const fmt = (n) => (n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 });

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;
  if (!data) return null;
  const { cantieri, totali } = data;

  return (
    <div className="space-y-6" data-testid="finanze-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Situazione Finanziaria</h1>
        <p className="text-slate-500 mt-1">Riepilogo economico, fatturato annuale e fatture XML</p>
      </div>

      {/* Totali globali */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-slate-200 bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1"><Receipt className="h-4 w-4 text-blue-600" /><p className="text-xs uppercase tracking-wider text-blue-600 font-medium">Fatturato Totale</p></div>
            <p className="text-2xl font-bold text-blue-700" data-testid="totale-fatturato">{fmt(totali.fatturato)}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 bg-emerald-50 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1"><CreditCard className="h-4 w-4 text-emerald-600" /><p className="text-xs uppercase tracking-wider text-emerald-600 font-medium">Incassato</p></div>
            <p className="text-2xl font-bold text-emerald-700" data-testid="totale-incassato">{fmt(totali.incassato)}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1"><Wallet className="h-4 w-4 text-amber-600" /><p className="text-xs uppercase tracking-wider text-amber-600 font-medium">Da Incassare</p></div>
            <p className="text-2xl font-bold text-amber-700" data-testid="totale-da-incassare">{fmt(totali.da_incassare)}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 bg-red-50 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1"><TrendingDown className="h-4 w-4 text-red-600" /><p className="text-xs uppercase tracking-wider text-red-600 font-medium">Costi Reali (fatture pagate)</p></div>
            <p className="text-2xl font-bold text-red-700" data-testid="totale-costi">{fmt(totali.totale_costi)}</p>
            {typeof totali.costi_manuali === 'number' && (
              <p className="text-[10px] text-red-500 mt-0.5" title="Materiali + Costi Operai + Costi extra registrati manualmente sui cantieri">
                Manuali: <span className="font-semibold">{fmt(totali.costi_manuali)}</span>
              </p>
            )}
          </CardContent>
        </Card>
        <Card className={`border-slate-200 ${totali.utile >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1"><TrendingUp className={`h-4 w-4 ${totali.utile >= 0 ? 'text-green-600' : 'text-red-600'}`} /><p className={`text-xs uppercase tracking-wider font-medium ${totali.utile >= 0 ? 'text-green-600' : 'text-red-600'}`}>Utile Netto</p></div>
            <p className={`text-2xl font-bold ${totali.utile >= 0 ? 'text-green-700' : 'text-red-700'}`} data-testid="totale-utile">{fmt(totali.utile)}</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="cantieri">
        <TabsList>
          <TabsTrigger value="cantieri" data-testid="tab-cantieri"><Building2 className="h-4 w-4 mr-1.5" />Per Cantiere</TabsTrigger>
          <TabsTrigger value="annuale" data-testid="tab-annuale"><Receipt className="h-4 w-4 mr-1.5" />Fatturato Annuale</TabsTrigger>
          <TabsTrigger value="xml" data-testid="tab-xml"><FileText className="h-4 w-4 mr-1.5" />Fatture XML</TabsTrigger>
        </TabsList>

        {/* Per Cantiere */}
        <TabsContent value="cantieri">
          <Card className="border-slate-200">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Cantiere</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Cliente</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Fatturato</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Incassato</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Da Incassare</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right" title="Costi reali: fatture ricevute effettivamente pagate collegate al cantiere">Costi Reali</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right" title="Costi manuali: materiali + costi operai + costi extra registrati sui cantieri">Costi Manuali</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Utile</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cantieri.map((c) => (
                    <TableRow key={c.cantiere_id} className="border-slate-100 hover:bg-slate-50">
                      <TableCell className="font-medium text-slate-900">{c.cantiere_name}</TableCell>
                      <TableCell className="text-slate-600">{c.client_name || '-'}</TableCell>
                      <TableCell className="text-right font-medium text-blue-700">{fmt(c.fatturato)}</TableCell>
                      <TableCell className="text-right font-medium text-emerald-700">{fmt(c.incassato)}</TableCell>
                      <TableCell className={`text-right font-medium ${c.da_incassare > 0 ? 'text-amber-700' : 'text-slate-400'}`}>{fmt(c.da_incassare)}</TableCell>
                      <TableCell className="text-right text-red-700 font-medium" title="Fatture ricevute pagate collegate al cantiere">{fmt(c.costi_da_fatture ?? c.totale_costi)}</TableCell>
                      <TableCell className="text-right text-slate-500" title="Materiali + operai + costi extra manuali">{fmt(c.costi_manuali ?? 0)}</TableCell>
                      <TableCell className={`text-right font-bold ${c.utile >= 0 ? 'text-green-700' : 'text-red-700'}`}>{fmt(c.utile)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-slate-100 border-t-2 border-slate-300">
                    <TableCell className="font-bold" colSpan={2}>TOTALE</TableCell>
                    <TableCell className="text-right font-bold text-blue-700">{fmt(totali.fatturato)}</TableCell>
                    <TableCell className="text-right font-bold text-emerald-700">{fmt(totali.incassato)}</TableCell>
                    <TableCell className="text-right font-bold text-amber-700">{fmt(totali.da_incassare)}</TableCell>
                    <TableCell className="text-right font-bold text-red-700">{fmt(totali.costi_da_fatture ?? totali.totale_costi)}</TableCell>
                    <TableCell className="text-right font-bold text-slate-500">{fmt(totali.costi_manuali ?? 0)}</TableCell>
                    <TableCell className={`text-right font-bold ${totali.utile >= 0 ? 'text-green-700' : 'text-red-700'}`}>{fmt(totali.utile)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Fatturato Annuale */}
        <TabsContent value="annuale">
          <Card className="border-slate-200">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Anno</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Fatturato Emesso</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Incassato</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Da Incassare</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">N. Fatture</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Fatture XML (Entrata)</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">N. Fatt. XML</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {annuale.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Nessun dato disponibile</TableCell></TableRow>
                  ) : annuale.map((a) => (
                    <TableRow key={a.anno} className="border-slate-100">
                      <TableCell className="font-bold text-slate-900 text-lg">{a.anno}</TableCell>
                      <TableCell className="text-right font-medium text-blue-700">{fmt(a.fatturato_emesso)}</TableCell>
                      <TableCell className="text-right font-medium text-emerald-700">{fmt(a.incassato)}</TableCell>
                      <TableCell className={`text-right font-medium ${a.da_incassare > 0 ? 'text-amber-700' : 'text-slate-400'}`}>{fmt(a.da_incassare)}</TableCell>
                      <TableCell className="text-right text-slate-600">{a.num_fatture}</TableCell>
                      <TableCell className="text-right font-medium text-purple-700">{fmt(a.fatture_xml_totale)}</TableCell>
                      <TableCell className="text-right text-slate-600">{a.num_fatture_xml}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Fatture XML */}
        <TabsContent value="xml">
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2"><FileText className="h-5 w-5 text-purple-600" />Fatture Elettroniche (XML)</CardTitle>
                <label>
                  <input type="file" accept=".xml" onChange={handleUploadXml} className="hidden" data-testid="xml-upload-input" />
                  <Button asChild className="bg-slate-900 text-white hover:bg-slate-800 cursor-pointer" disabled={uploading}>
                    <span><Upload className="h-4 w-4 mr-2" />{uploading ? 'Importazione...' : 'Importa XML'}</span>
                  </Button>
                </label>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">N. Fattura</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Data</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Cedente/Fornitore</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Imponibile</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">IVA</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Totale</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fattureXml.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      <Upload className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                      Nessuna fattura XML importata. Carica un file FatturaPA (.xml)
                    </TableCell></TableRow>
                  ) : fattureXml.map((fx) => (
                    <TableRow key={fx.id} className="border-slate-100">
                      <TableCell className="font-mono font-medium text-slate-900">{fx.numero || '-'}</TableCell>
                      <TableCell className="text-slate-600">{fx.data || '-'}</TableCell>
                      <TableCell className="text-slate-900 max-w-xs truncate">{fx.cedente || '-'}</TableCell>
                      <TableCell className="text-right text-slate-600">{fmt(fx.imponibile)}</TableCell>
                      <TableCell className="text-right text-slate-600">{fmt(fx.imposta)}</TableCell>
                      <TableCell className="text-right font-bold text-purple-700">{fmt(fx.importo_totale)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600" onClick={() => { setSelectedXml(fx); setXmlDetailOpen(true); }} data-testid={`view-xml-${fx.id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => handleDeleteXml(fx.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* XML Detail Dialog */}
      <Dialog open={xmlDetailOpen} onOpenChange={setXmlDetailOpen}>
        <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dettaglio Fattura {selectedXml?.numero}</DialogTitle>
          </DialogHeader>
          {selectedXml && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg border"><p className="text-xs text-slate-500">Cedente/Fornitore</p><p className="font-medium">{selectedXml.cedente || '-'}</p>{selectedXml.cedente_piva && <p className="text-xs text-slate-400">P.IVA: {selectedXml.cedente_piva}</p>}</div>
                <div className="p-3 bg-slate-50 rounded-lg border"><p className="text-xs text-slate-500">Cessionario</p><p className="font-medium">{selectedXml.cessionario || '-'}</p></div>
                <div className="p-3 bg-slate-50 rounded-lg border"><p className="text-xs text-slate-500">Numero</p><p className="font-medium">{selectedXml.numero}</p></div>
                <div className="p-3 bg-slate-50 rounded-lg border"><p className="text-xs text-slate-500">Data</p><p className="font-medium">{selectedXml.data}</p></div>
              </div>
              {selectedXml.items?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">Dettaglio Voci</p>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs">Descrizione</TableHead>
                        <TableHead className="text-xs text-right">Qta</TableHead>
                        <TableHead className="text-xs text-right">Prezzo Unit.</TableHead>
                        <TableHead className="text-xs text-right">Totale</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedXml.items.map((item, idx) => (
                        <TableRow key={`${item.descrizione || 'item'}-${idx}`}>
                          <TableCell className="text-sm">{item.descrizione || '-'}</TableCell>
                          <TableCell className="text-right text-sm">{item.quantita || '-'}</TableCell>
                          <TableCell className="text-right text-sm">{fmt(item.prezzo_unitario)}</TableCell>
                          <TableCell className="text-right font-medium text-sm">{fmt(item.prezzo_totale)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3 pt-2 border-t">
                <div className="p-3 bg-slate-50 rounded-lg text-center"><p className="text-xs text-slate-500">Imponibile</p><p className="font-bold">{fmt(selectedXml.imponibile)}</p></div>
                <div className="p-3 bg-slate-50 rounded-lg text-center"><p className="text-xs text-slate-500">IVA</p><p className="font-bold">{fmt(selectedXml.imposta)}</p></div>
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 text-center"><p className="text-xs text-purple-600">Totale</p><p className="font-bold text-purple-700 text-lg">{fmt(selectedXml.importo_totale)}</p></div>
              </div>
              <p className="text-xs text-slate-400">File: {selectedXml.filename}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
